import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api, { errMsg, keepaliveJson } from "../api";

function Saved({ msg }) {
  if (!msg) return null;
  return <span className={msg.ok ? "llh-fs-13 llh-c-done llh-ml-12" : "llh-fs-13 llh-c-crimson llh-ml-12"}>{msg.text}</span>;
}

function Toggle({ on, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} role="switch" aria-checked={on}
      className={on ? "llh-switch llh-switch--on" : "llh-switch"}>
      <span className={on ? "llh-switch-knob llh-switch-knob--on" : "llh-switch-knob"} />
    </button>
  );
}

export default function Account() {
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [bw, setBw] = useState([]);
  const [loadErr, setLoadErr] = useState("");

  // form state
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [ft, setFt] = useState("");
  const [inch, setInch] = useState("");
  const [age, setAge] = useState("");
  const [goals, setGoals] = useState("");
  const [notes, setNotes] = useState("");
  const [profMsg, setProfMsg] = useState(null);
  const [savingProf, setSavingProf] = useState(false);

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwMsg, setPwMsg] = useState(null);

  const [bwInput, setBwInput] = useState("");
  const [bwMsg, setBwMsg] = useState(null);

  const [privacy, setPrivacy] = useState(null); // { share_progress, share_notes }
  const [privMsg, setPrivMsg] = useState(null);

  const loadBw = () => api.get("/bodyweight").then(({ data }) => setBw(data)).catch(() => {});

  useEffect(() => {
    api.get("/profile").then(({ data }) => {
      setProfile(data);
      setEmail(data.email || "");
      setDisplayName(data.display_name || "");
      if (data.height_in != null) { setFt(String(Math.floor(data.height_in / 12))); setInch(String(Math.round(data.height_in % 12))); }
      setAge(data.age != null ? String(data.age) : "");
      setGoals(data.goals || "");
      setNotes(data.notes || "");
    }).catch((e) => setLoadErr(errMsg(e)));
    loadBw();
    api.get("/settings").then(({ data }) => {
      setPrivacy({
        share_progress: !!(data && data.share_progress),
        share_notes: !!(data && data.share_notes),
      });
    }).catch(() => setPrivacy({ share_progress: false, share_notes: false }));
  }, []);

  const togglePrivacy = async (key) => {
    const prev = privacy;
    const next = { ...privacy, [key]: !privacy[key] };
    setPrivacy(next);
    setPrivMsg(null);
    try {
      await keepaliveJson("PUT", "/settings", { [key]: next[key] });
      setPrivMsg({ ok: true, text: "Saved" });
    } catch (e) {
      setPrivacy(prev); // revert on failure
      setPrivMsg({ ok: false, text: errMsg(e, "Could not save") });
    }
  };

  const saveProfile = async () => {
    if (displayName.trim() === "") { setProfMsg({ ok: false, text: "Display name is required" }); return; }
    setSavingProf(true); setProfMsg(null);
    const height_in = ft === "" && inch === "" ? null : (Number(ft || 0) * 12 + Number(inch || 0));
    try {
      await keepaliveJson("PUT", "/profile", {
        email: email.trim(),
        display_name: displayName.trim(),
        height_in,
        age: age === "" ? null : Number(age),
        goals: goals.trim() || null,
        notes: notes.trim() || null,
      });
      setProfMsg({ ok: true, text: "Saved" });
    } catch (e) { setProfMsg({ ok: false, text: errMsg(e, "Could not save") }); }
    finally { setSavingProf(false); }
  };

  const changePw = async () => {
    setPwMsg(null);
    if (newPw.length < 6) { setPwMsg({ ok: false, text: "New password must be at least 6 characters" }); return; }
    try {
      await api.put("/auth/password", { current_password: curPw, new_password: newPw });
      setPwMsg({ ok: true, text: "Password changed" });
      setCurPw(""); setNewPw("");
    } catch (e) { setPwMsg({ ok: false, text: errMsg(e, "Could not change password") }); }
  };

  const logBw = async () => {
    setBwMsg(null);
    const v = Number(bwInput);
    if (!v || v <= 0) { setBwMsg({ ok: false, text: "Enter a weight" }); return; }
    try {
      await keepaliveJson("POST", "/bodyweight", { weight_lb: v });
      setBwInput(""); setBwMsg({ ok: true, text: "Logged" });
      loadBw();
    } catch (e) { setBwMsg({ ok: false, text: errMsg(e) }); }
  };

  const delBw = async (id) => {
    try { await api.delete(`/bodyweight/${id}`); loadBw(); } catch (e) { alert(errMsg(e)); }
  };

  const chart = bw.map((r) => ({ date: String(r.logged_date).slice(5, 10), weight: Number(r.weight_lb) }));
  const latest = bw.length ? Number(bw[bw.length - 1].weight_lb) : null;

  return (
    <div className="llh-page llh-pb-40">
      <header className="llh-header-sticky">
        <div className="llh-header-inner">
          <button onClick={() => nav("/app")} className="llh-navlink llh-btn-reset">
            <span className="llh-nav-caret">‹</span> Back
          </button>
          <div className="llh-header-title">ACCOUNT</div>
          <div className="llh-w-50" />
        </div>
      </header>

      <main className="llh-account-main">
        {loadErr && <div className="llh-c-crimson llh-mb-12">{loadErr}</div>}
        {!profile ? <div className="llh-spin" /> : (
          <>
            <div className="llh-card llh-card--roomy">
              <div className="llh-eyebrow llh-mb-12">Profile</div>
              <div className="llh-mb-14">
                <label className="llh-label">Username</label>
                <div className="llh-input llh-input-readonly">{profile.username}</div>
              </div>
              <label className="llh-label">Display name</label>
              <input className="llh-input llh-mb-4" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="The name you're known by at the school" />
              <div className="llh-field-note llh-mb-14">This is how you appear to your coaches. Use the name you're known by at the school.</div>
              <label className="llh-label">Email</label>
              <input className="llh-input llh-mb-14" value={email} autoCapitalize="none" onChange={(e) => setEmail(e.target.value)} />

              <div className="llh-field-row llh-mb-14">
                <div className="llh-flex-1">
                  <label className="llh-label">Height</label>
                  <div className="llh-inline-row">
                    <input className="llh-input llh-w-70" type="number" placeholder="ft" value={ft} onChange={(e) => setFt(e.target.value)} />
                    <span className="llh-dim">ft</span>
                    <input className="llh-input llh-w-70" type="number" placeholder="in" value={inch} onChange={(e) => setInch(e.target.value)} />
                    <span className="llh-dim">in</span>
                  </div>
                </div>
                <div className="llh-w-110">
                  <label className="llh-label">Age</label>
                  <input className="llh-input" type="number" value={age} onChange={(e) => setAge(e.target.value)} />
                </div>
              </div>

              <label className="llh-label">Goals</label>
              <textarea className="llh-input llh-textarea-input llh-mb-14" value={goals} onChange={(e) => setGoals(e.target.value)} placeholder="What you're training toward" />

              <label className="llh-label">Notes (injuries, anything relevant)</label>
              <textarea className="llh-input llh-textarea-input llh-minh-70 llh-mb-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. left side tightness on heavy squat" />

              <div className="llh-row-mid">
                <button className="llh-btn-primary llh-btn--dim6" disabled={savingProf} onClick={saveProfile}>{savingProf ? "Saving..." : "Save profile"}</button>
                <Saved msg={profMsg} />
              </div>
            </div>

            <div className="llh-card llh-card--roomy">
              <div className="llh-eyebrow llh-mb-12">Privacy</div>
              <p className="llh-priv-note">
                Both are off by default. Turn these on to let your coach review your training.
              </p>
              {privacy === null ? <div className="llh-spin" /> : (
                <>
                  <div className="llh-toggle-row">
                    <div>
                      <div className="llh-toggle-label">Share workout progress</div>
                      <div className="llh-toggle-desc">Let coaches see your logged sessions and lifts.</div>
                    </div>
                    <Toggle on={privacy.share_progress} onClick={() => togglePrivacy("share_progress")} />
                  </div>
                  <div className="llh-toggle-row llh-border-top">
                    <div>
                      <div className="llh-toggle-label">Share session notes</div>
                      <div className="llh-toggle-desc">Include your written notes when a coach reviews a session.</div>
                    </div>
                    <Toggle on={privacy.share_notes} onClick={() => togglePrivacy("share_notes")} />
                  </div>
                  <div className="llh-mt-4"><Saved msg={privMsg} /></div>
                </>
              )}
            </div>

            <div className="llh-card llh-card--roomy">
              <div className="llh-eyebrow llh-mb-12">Bodyweight</div>
              <div className="llh-row-gap10 llh-mb-14">
                <input className="llh-input llh-w-130" type="number" placeholder="lb" value={bwInput} onChange={(e) => setBwInput(e.target.value)} />
                <button className="llh-btn-primary" onClick={logBw}>Log today</button>
                <Saved msg={bwMsg} />
              </div>
              {latest != null && <div className="llh-dim-13 llh-mb-10">Latest: <span className="llh-val">{latest} lb</span></div>}
              {chart.length >= 2 ? (
                <div className="llh-h-200">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chart} margin={{ top: 4, right: 14, bottom: 0, left: -12 }}>
                      <CartesianGrid stroke="var(--line)" strokeDasharray="2 4" vertical={false} />
                      <XAxis dataKey="date" tick={{ fill: "var(--dim)", fontSize: 10 }} interval="preserveStartEnd" minTickGap={26} stroke="var(--line)" />
                      <YAxis tick={{ fill: "var(--dim)", fontSize: 10 }} stroke="var(--line)" width={38} domain={["dataMin - 3", "dataMax + 3"]} />
                      <Tooltip contentStyle={{ background: "var(--raised)", border: "1px solid var(--line)", borderRadius: 8, color: "var(--text)" }} labelStyle={{ color: "var(--dim)" }} />
                      <Line type="monotone" dataKey="weight" stroke="var(--brass)" strokeWidth={2.5} dot={{ r: 2, fill: "var(--brass)" }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <div className="llh-dim-13">Log at least two entries to see your trend.</div>}

              {bw.length > 0 && (
                <div className="llh-bw-list">
                  {[...bw].reverse().map((r) => (
                    <div key={r.id} className="llh-bw-item">
                      <span className="llh-dim">{String(r.logged_date).slice(0, 10)}</span>
                      <span className="llh-row-gap10">
                        <span className="llh-num-bold">{Number(r.weight_lb)} lb</span>
                        <button onClick={() => delBw(r.id)} className="llh-btn-x" aria-label="delete entry">×</button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="llh-card llh-card--roomy">
              <div className="llh-eyebrow llh-mb-12">Change password</div>
              <label className="llh-label">Current password</label>
              <input className="llh-input llh-mb-12" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} />
              <label className="llh-label">New password</label>
              <input className="llh-input llh-mb-14" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
              <div className="llh-row-mid">
                <button className="llh-btn-ghost" onClick={changePw}>Update password</button>
                <Saved msg={pwMsg} />
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
